import type { Coordinates } from "./types";

export interface CampusConfig {
  id: string;
  name: { en: string; th: string; zh: string };
  /** Short form for the phone header, where the full name does not fit
   *  beside the controls. Measured at 390px: the Thai name needs 99px and
   *  the English 140px, and the header could give the title 96px — so the
   *  app's own name was rendered as "เทศบาลนครยะ…". */
  shortName?: { en: string; th: string };
  center: Coordinates;
  innerBounds: [Coordinates, Coordinates];
  outerBounds: [Coordinates, Coordinates];
  surroundingRoads: string[];
  defaultView: {
    longitude: number;
    latitude: number;
    zoom: number;
    pitch: number;
    bearing: number;
  };
}

export const YALA: CampusConfig = {
  id: "yala-city",
  name: {
    en: "Yala City Municipality",
    th: "เทศบาลนครยะลา",
    zh: "惹拉市",
  },
  shortName: {
    en: "Yala City",
    th: "เทศบาลยะลา",
  },
  // เทศบาลนครยะลา (Yala City Municipality) — ~19 km².
  // Thailand's only planned concentric-circular city: ring roads (Wongwian 1/2/3)
  // radiate from a central roundabout park ("Kilometre Zero"). The Pattani River
  // runs along the western edge of the core. Provincial capital of Yala — one of
  // the three "Deep South" border provinces.
  center: [101.2831, 6.5425], // [lng, lat] — central circle / Kilometre Zero
  innerBounds: [
    [101.270, 6.530], // SW — ~1.6 km from centre
    [101.297, 6.556], // NE — ~1.6 km from centre
  ],
  outerBounds: [
    [101.255, 6.515], // overscroll toward the river + station, not the whole province
    [101.312, 6.572],
  ],
  surroundingRoads: [
    "Phang Mueang Road",   // central radial spine
    "Wongwian 1 / 2 / 3",  // concentric ring roads
    "Sirorot Road",
    "Pipitpakdee Road",
    "Ruammit Road",
  ],
  // bearing 0 (vs siblings' off-axis) so the radial circular street grid reads
  // symmetrically — Yala's defining visual signature. pitch 62° extrudes even
  // 2-storey shophouses around the circle.
  defaultView: {
    longitude: 101.2831,
    latitude: 6.5425,
    zoom: 14.6,
    pitch: 62,
    bearing: 0,
  },
};

// No `CHONBURI` / `CHULA` back-compat aliases. They existed to keep "stray"
// references building during the Chonburi/Chula → Yala fork, but they made
// the migration self-perpetuating: the aliases justified the seven files that
// imported `CHONBURI.center` for a Yala dashboard, and those files justified
// the aliases. Coordinates were never wrong (`CHONBURI` was the same object as
// `YALA`), but a Yala control tower computing Yala's centre from a constant
// named after another province is a trap for the next reader. Those seven
// files now import YALA directly. Historical references to Chonburi in
// comments and in real upstream resource names
// (`GISDTA_BLDGLOD2_Solar_TimeSlider_Chonburi_Month`) are deliberately left
// alone — they name something that genuinely is Chonburi.
